import { type MouseEvent, useCallback, useLayoutEffect, useRef, useState } from 'react'
import {
  CloseButton,
  ExpandButtonActual,
  ExpandButtonPlaceholder,
  VideoTitle,
  VIDEO_TITLE_LINE_COUNT,
} from './Styled'

interface FoldableVideoTitleProps {
  text: string
}

const FoldableVideoTitle = ({ text }: FoldableVideoTitleProps) => {
  const containerRef = useRef<HTMLAnchorElement>(null)
  const textRef = useRef<HTMLSpanElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [expandButtonVisible, setExpandButtonVisible] = useState(false)
  const [expandButtonPos, setExpandButtonPos] = useState<[number, number]>([0, 0])

  const toggleExpand = useCallback(
    (ev: MouseEvent) => {
      ev.preventDefault()
      ev.stopPropagation()

      if (!expandButtonVisible) {
        return
      }

      if (expanded) {
        setExpandButtonPos([0, 0])
      }

      setExpanded((value) => !value)
    },
    [expandButtonVisible, expanded]
  )

  useLayoutEffect(() => {
    setExpanded(false)
    setExpandButtonVisible(false)
    setExpandButtonPos([0, 0])
  }, [text])

  useLayoutEffect(() => {
    const textEl = textRef.current
    const containerEl = containerRef.current

    if (textEl == null || containerEl == null) {
      return
    }

    const handler = () => {
      const lineHeight = Number(getComputedStyle(containerEl).lineHeight.replace('px', ''))

      if (!Number.isFinite(lineHeight) || lineHeight === 0) {
        return
      }

      const hasOverflow = containerEl.scrollHeight > lineHeight * VIDEO_TITLE_LINE_COUNT + 1
      setExpandButtonVisible(hasOverflow)

      if (!hasOverflow || expanded) {
        setExpandButtonPos([0, 0])
        return
      }

      const base = containerEl.getBoundingClientRect()
      const boxes = [...textEl.querySelectorAll(':scope > *')]
        .filter((el) => el.textContent?.trim() !== '')
        .flatMap((el) => [...el.getClientRects()])
        .map((rect) => ({
          left: rect.left - base.left,
          top: rect.top - base.top,
          width: rect.width,
          height: rect.height,
        }))

      const maxY = (VIDEO_TITLE_LINE_COUNT - 0.5) * lineHeight
      const visibleBoxes = boxes
        .filter((box) => box.top < maxY)
        .sort((a, b) => {
          const aLineNum = Math.floor((a.top + a.height / 2) / lineHeight)
          const bLineNum = Math.floor((b.top + b.height / 2) / lineHeight)

          if (aLineNum - bLineNum !== 0) {
            return aLineNum - bLineNum
          }

          return a.left + a.width - (b.left + b.width)
        })

      const lastBox = visibleBoxes[visibleBoxes.length - 1]

      if (lastBox == null) {
        return
      }

      setExpandButtonPos([lastBox.left + lastBox.width, lastBox.top + lastBox.height / 2])
    }

    const observer = new ResizeObserver(handler)
    observer.observe(textEl)
    observer.observe(containerEl)
    handler()

    return () => {
      observer.disconnect()
    }
  }, [expanded, text])

  const textParts = text
    .split(/( )/g)
    .map((part, index) => (part === '' ? null : <span key={`${index}_${part}`}>{part}</span>))

  return (
    <VideoTitle ref={containerRef} $expanded={expanded} $clickable={expandButtonVisible} onClick={toggleExpand}>
      {expandButtonVisible && !expanded && <ExpandButtonPlaceholder>...展开</ExpandButtonPlaceholder>}
      {expandButtonPos[0] !== 0 && expandButtonPos[1] !== 0 && (
        <ExpandButtonActual $left={expandButtonPos[0]} $top={expandButtonPos[1]} $hidden={!expandButtonVisible || expanded}>
          ...展开
        </ExpandButtonActual>
      )}
      <span ref={textRef}>{textParts}</span>
      {expandButtonVisible && expanded && <CloseButton>收起</CloseButton>}
    </VideoTitle>
  )
}

export default FoldableVideoTitle
